import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faHeadset, faCalendarCheck } from '@fortawesome/free-solid-svg-icons';
import cx from 'classnames';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber, formatPhoneNumber } from 'react-phone-number-input/max';
import 'react-phone-number-input/style.css';
import { getDeviceType } from '../../../utils';
import { BUSINESS_HOURS, isWithinBusinessHours } from './businessHours';
import './InstantConnect.scss';

const DEPARTMENT_PHONE_NUMBER = '+19192992847';
const DEPARTMENT_PHONE_DISPLAY = '(919) 299-2847';

const MEETINGS_LINK = 'https://meetings.hubspot.com/mike-agesen/lysted-instant-connect-demo-call';

// A tappable tel: link only places a call on an actual phone (or tablet), so
// gate the "Call now" button on the true device (user agent), not the
// viewport-based isMobile layout prop. "Not desktop" keeps tablets on the
// tappable link even though getDeviceType now reports them as 'tablet'.
// Desktop visitors get the number as readable text. Computed once at module
// load — the user agent doesn't change within a session, so there's no need
// to re-run the regex test on every render.
const IS_MOBILE_DEVICE = getDeviceType() !== 'desktop';

// HubSpot's public (non-embedded) meeting links prefill via lowercase
// `firstname`/`lastname`/`email` query params matching the contact property
// internal names; any other casing/name is silently ignored.
const buildMeetingsLink = (firstName, lastName, email) => {
    const params = new URLSearchParams();
    if (firstName) { params.set('firstname', firstName); }
    if (lastName) { params.set('lastname', lastName); }
    if (email) { params.set('email', email); }
    const query = params.toString();
    return query ? `${MEETINGS_LINK}?${query}` : MEETINGS_LINK;
};

function InstantConnect({ isMobile, phone, firstName, lastName, email, onInteraction }) {
    // `phone` arrives as an E.164 string (e.g. "+447911123456" or "+17283102423")
    // from the main signup phone field, already validated there by isValidPhoneNumber.
    // Prefill the callback field with it and let the broker edit to a different
    // number in any country. PhoneInput keeps the value in E.164, so it passes
    // straight through to HubSpot with no US-specific reformatting. The main
    // signup form uses this same component, so the callback field matches it.
    const [callbackPhone, setCallbackPhone] = useState(phone || '');
    const [callbackPhoneError, setCallbackPhoneError] = useState(false);
    const [callbackSubmitted, setCallbackSubmitted] = useState(false);
    // True while the request is in flight to our backend, so the button can
    // show progress instead of appearing to do nothing during the await.
    const [callbackSending, setCallbackSending] = useState(false);
    // Set when onInteraction reports the backend call failed (network error,
    // validation, etc.) — show a way through instead of "We'll call you shortly".
    const [callbackFailed, setCallbackFailed] = useState(false);
    // Set when the submit-time cutoff re-check rejects a request the render-time
    // gate had still been showing (see handleCallbackSubmit).
    const [callbackClosed, setCallbackClosed] = useState(false);
    // Re-tick every 60s so the live options appear/disappear as the clock
    // crosses the business-hours boundaries — otherwise the flags would freeze
    // at mount time (e.g. someone who opened signup before 9 AM and reached
    // this step during business hours would see no live options, and someone
    // lingering past a cutoff would keep seeing a stale one).
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const tick = () => setNow(new Date());
        const intervalId = setInterval(tick, 60 * 1000);
        // A 60s interval alone isn't enough to keep a closed option off screen.
        // Browsers throttle timers in background tabs, and a sleeping device
        // doesn't fire them at all, so a tab left open across a cutoff can come
        // back showing an option that is no longer available. Re-read the clock
        // the moment the page is looked at again rather than waiting for the next
        // scheduled tick.
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') { tick(); }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', tick);
        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', tick);
        };
    }, []);
    // Each live option has its own earlier cutoff so a rep isn't started on a
    // fresh call/callback right before the office closes (see businessHours.js).
    const showCallNow = isWithinBusinessHours(now, BUSINESS_HOURS.callNowEnd);
    const showCallback = isWithinBusinessHours(now, BUSINESS_HOURS.callbackEnd);
    // The office is open later than either per-option cutoff (17:00 vs 16:30 /
    // 15:30), so "no live options" is not the same as "nobody is here" — between
    // the last cutoff and close, reps are still at their desks. Read off the same
    // 60s timer as the flags above so all three tick together.
    const officeOpen = isWithinBusinessHours(now, BUSINESS_HOURS.end);
    // Outside the per-option cutoffs both live options hide, leaving only
    // Schedule. Drive the note off the same flags so it stays in sync with the
    // 60s timer rather than drifting to a separate condition.
    const noLiveOptions = !showCallNow && !showCallback;
    // Memoized so the 60s business-hours timer's re-render doesn't rebuild the
    // URL every minute; only recomputes when the prefill inputs change.
    const meetingsLink = useMemo(
        () => buildMeetingsLink(firstName, lastName, email),
        [firstName, lastName, email],
    );

    // onInteraction fires HubSpot tracking that transmits asynchronously via the
    // pageview flush. On the mobile `tel:` link the browser backgrounds the page
    // the instant it's tapped, which can cut that beacon off mid-flight and lose
    // the `call_now` event. We can't swap HubSpot's transport (its script, loaded
    // via GTM, owns the actual send off the `_hsq` queue), so instead we defer the
    // dial: prevent the default navigation, queue the tracking, then hand off to
    // `tel:` after a frame + a short delay so the page stays foregrounded long
    // enough for the beacon to leave. The href stays a real `tel:` link so
    // long-press / copy still work. Desktop shows the number as text and never
    // hits this handler.
    const CALL_NOW_DIAL_DELAY_MS = 150;
    const handleCallNowClick = (e) => {
        e.preventDefault();
        onInteraction?.('call_now');
        const dial = () => { window.location.href = `tel:${DEPARTMENT_PHONE_NUMBER}`; };
        // rAF lets the synchronous `_hsq` push settle; the timeout gives HubSpot's
        // async beacon a brief window to fire before the `tel:` handoff backgrounds us.
        window.requestAnimationFrame(() => window.setTimeout(dial, CALL_NOW_DIAL_DELAY_MS));
    };

    // Synchronous re-entry guard: `callbackSubmitted` is set via setState (async),
    // so a fast double-click could fire onInteraction twice before it flips,
    // creating a duplicate callback signal for Sales. Set the ref only after
    // validation passes so a failed attempt doesn't lock the user out.
    const submittingRef = useRef(false);

    const handleCallbackSubmit = async () => {
        if (submittingRef.current) { return; }
        // Re-gate on a fresh clock rather than the `now` state. The render-time
        // gate is only as current as the last tick, so a throttled or slept tab
        // can leave this form on screen past the cutoff. The cutoff exists so a
        // rep isn't started on a fresh callback close to the end of the day, which
        // means it has to hold at the moment of submit, not just at the moment of
        // render. Checked before phone validation — no reason to make them fix a
        // number we aren't going to accept.
        if (!isWithinBusinessHours(new Date(), BUSINESS_HOURS.callbackEnd)) {
            submittingRef.current = true;
            setCallbackClosed(true);
            return;
        }
        // callbackPhone is already E.164 from PhoneInput; validate for any country
        // and pass it straight through rather than assuming a US (+1) number.
        if (!callbackPhone || !isValidPhoneNumber(callbackPhone)) {
            setCallbackPhoneError(true);
            return;
        }
        submittingRef.current = true;
        setCallbackSending(true);
        // Strict `=== true`: a missing onInteraction prop yields undefined, which
        // must count as a failure rather than a promise we can't keep.
        const sent = await onInteraction?.('request_callback', { phone: callbackPhone });
        setCallbackSending(false);
        if (sent === true) {
            setCallbackSubmitted(true);
            return;
        }
        // Leave submittingRef set. A retry would just repeat whatever backend
        // error just happened, and re-promise a call in between.
        setCallbackFailed(true);
    };

    // Same device gate as Call now: a tel: link only dials on a real phone or
    // tablet, so desktop gets the number as plain emphasized text. No deferred
    // dial here — the tracking that handleCallNowClick protects is precisely
    // what has already failed (or is known unavailable) on these paths.
    const departmentPhoneNode = IS_MOBILE_DEVICE ? (
        <a href={`tel:${DEPARTMENT_PHONE_NUMBER}`} className="fallback-tel">
            {DEPARTMENT_PHONE_DISPLAY}
        </a>
    ) : (
        <strong>{DEPARTMENT_PHONE_DISPLAY}</strong>
    );

    // Lower risk than call_now (see note above): the Schedule link opens in a
    // new tab (target="_blank"), so the original signup tab stays alive and the
    // tracking XHR completes normally.
    const handleBookDemoClick = () => {
        onInteraction?.('schedule_demo');
    };

    return (
        <div className={cx('instant-connect', { mobile: isMobile })}>
            {/* The heading ("Talk to our team") is owned by the completion page
                that hosts this block, so it can carry the "Optional" pill. */}
            {noLiveOptions && (
                officeOpen ? (
                    <p className="instant-connect-offline-note">
                        Our team is wrapping up calls for today. Book a time below that
                        works for you and we'll be ready when you are.
                    </p>
                ) : (
                    <p className="instant-connect-offline-note">
                        Our team is offline right now. Book a time below that works for you
                        and we'll follow up with you.
                    </p>
                )
            )}
            <div className="instant-connect-intro">
                <p className="intro-highlight">
                    As a high-volume seller, you may qualify for our volume discount tiers — 
                    <strong> up to 40% off selling fees</strong>. Talk to our team to learn more and
                    see if you qualify.
                </p>
                <p className="intro-support">
                    You'll also receive priority onboarding and work directly with a dedicated onboarding
                    specialist to help you get up and running quickly.
                </p>
            </div>
            <div className="instant-connect-options">
                {showCallNow && (
                    <div className="instant-connect-option">
                        <FontAwesomeIcon icon={faPhone} className="option-icon" />
                        <div className="option-body">
                            <h3>Call now</h3>
                            {IS_MOBILE_DEVICE ? (
                                <a
                                    href={`tel:${DEPARTMENT_PHONE_NUMBER}`}
                                    className="btn purple-transparent call-now-link"
                                    onClick={handleCallNowClick}
                                >
                                    {DEPARTMENT_PHONE_DISPLAY}
                                </a>
                            ) : (
                                <p className="call-now-number">
                                    Call us at <strong>{DEPARTMENT_PHONE_DISPLAY}</strong>
                                </p>
                            )}
                        </div>
                    </div>
                )}
                {/* The form itself is offered only while `showCallback` is true, so
                    a broker can never request a callback outside the window. The
                    three extra flags keep the option mounted *after* an attempt so
                    the response to it — confirmation, send failure, or closed-for-
                    today — doesn't vanish mid-read when the clock crosses the
                    cutoff. Each is set only by an action the broker already took;
                    none of them can re-open the form. */}
                {(showCallback || callbackSubmitted || callbackFailed || callbackClosed) && (
                    <div className="instant-connect-option">
                        <FontAwesomeIcon icon={faHeadset} className="option-icon" />
                        <div className="option-body">
                            <h3>Request a callback</h3>
                            {callbackSubmitted ? (
                                <p className="callback-confirmation">
                                    A member of our team will be in touch with you shortly at{' '}
                                    <strong>{formatPhoneNumber(callbackPhone) || callbackPhone}</strong>.
                                </p>
                            ) : callbackClosed ? (
                                <p className="callback-fallback">
                                    We've stopped taking new callbacks for today. Book a time
                                    below that works for you and we'll be ready when you are.
                                </p>
                            ) : callbackFailed ? (
                                <p className="callback-fallback">
                                    We couldn't submit your request. Call us at {departmentPhoneNode}{' '}
                                    and we'll help right away, or schedule a time below.
                                </p>
                            ) : (
                                <div className="callback-form">
                                    <div className={cx('form-field', { error: callbackPhoneError })}>
                                        <PhoneInput
                                            id="instant_connect_callback_phone"
                                            name="instant_connect_callback_phone"
                                            placeholder="Phone number"
                                            defaultCountry="US"
                                            value={callbackPhone}
                                            onChange={(value) => {
                                                setCallbackPhone(value || '');
                                                if (value && isValidPhoneNumber(value)) {
                                                    setCallbackPhoneError(false);
                                                }
                                            }}
                                            onBlur={() => {
                                                if (callbackPhone && !isValidPhoneNumber(callbackPhone)) {
                                                    setCallbackPhoneError(true);
                                                }
                                            }}
                                            disabled={callbackSending}
                                        />
                                        <label htmlFor="instant_connect_callback_phone">Phone number</label>
                                        {callbackPhoneError && <span className="error">Invalid phone number</span>}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn purple-transparent"
                                        onClick={handleCallbackSubmit}
                                        disabled={callbackSending}
                                    >
                                        {callbackSending ? 'Sending...' : 'Request a callback'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className="instant-connect-option">
                    <FontAwesomeIcon icon={faCalendarCheck} className="option-icon" />
                    <div className="option-body">
                        <h3>Set up a time for a different day</h3>
                        <a
                            href={meetingsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn purple-transparent"
                            onClick={handleBookDemoClick}
                        >
                            Schedule
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InstantConnect;
